import { fetchLayoutCategoriesAPI, fetchLayoutPackageDetailAPI, generatePreviewAPI } from '@/api/layout';
import { fetchLayoutPackageByStoreAPI } from '@/api/storeManagement/layout';
import { hideLoading, showLoading } from '@/components/ui/LoadingOverlay';
import { useAppContext } from '@/provider/contexts';
import { useLayoutState } from '@/provider/layouts/layoutContext';
import {
    FullLayoutItem,
    LayoutCategory,
    LayoutPackageDetails,
    MultiRelationsMap,
    SingleRelationsMap,
} from '@/types/pages/layout';
import { parseSizeAndColorsFromXml, xmlEscapedToBase64 } from '@/utils/layouts/layoutUtils';
import localforage from 'localforage';
import { findIndex } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export const useLayoutUtils = () => {
    const { t } = useTranslation('common');
    const { id } = useParams<{ id: string }>();
    const { manageStoreId, setNotification } = useAppContext();
    const { state, dispatch } = useLayoutState();
    const isNew = !id;

    const fetchCategories = async () => {
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'category', loading: true } });
        try {
            const corpId = await localforage.getItem<number>('corporation_id');
            if (!corpId) {
                dispatch({ type: 'SET_DISPLAY_OPTIONS', payload: [] });
                return;
            }
            const {
                data: { data },
            } = await fetchLayoutCategoriesAPI(corpId);
            const opts = data
                .filter((c: LayoutCategory) => c.category_identifier && c.category_name)
                .map((c: LayoutCategory) => ({
                    size: c.image_resolution ?? '',
                    value: c.category_identifier.trim(),
                    label: c.category_name.trim(),
                    multiLayoutOptions: c.multi_layouts.filter((o) => o.capacity <= 4),
                }))
                .filter((o, i, arr) => i === arr.findIndex((x) => x.value === o.value));
            dispatch({ type: 'SET_DISPLAY_OPTIONS', payload: opts });
        } catch {
            dispatch({ type: 'SET_DISPLAY_OPTIONS', payload: [] });
        } finally {
            dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'category', loading: false } });
            dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'layout', loading: false } });
        }
    };
    const fetchDetail = useCallback(async () => {
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'layout', loading: true } });
        try {
            const corpId = await localforage.getItem<number>('corporation_id');
            if (!corpId || !id) return;

            const {
                data: { data },
            } = await fetchLayoutPackageDetailAPI(corpId, +id);
            const { details, name, barcode_for_layout_preview_detail } = data;
            const payload: LayoutPackageDetails = details.packages;
            dispatch({ type: 'SET_LAYOUT_INFO', payload: payload });
            dispatch({ type: 'SET_PACKAGE_NAME', payload: name || '' });
            dispatch({ type: 'SET_PREVIEW_BARCODE_DETAIL', payload: barcode_for_layout_preview_detail || '' });
        } catch (err) {
            console.error('Failed to load package detail', err);
        } finally {
            dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'layout', loading: false } });
        }
    }, [id]);

    const fetchStoreDetail = useCallback(async () => {
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'layout', loading: true } });
        try {
            const corpId = await localforage.getItem<number>('corporation_id');
            if (!corpId || !manageStoreId) return;
            const {
                data: { data },
            } = await fetchLayoutPackageByStoreAPI(corpId, manageStoreId);
            const { details, lp_name, lp_version, has_package } = data || {};
            dispatch({ type: 'SET_LAYOUT_INFO', payload: details });
            dispatch({
                type: 'SET_PACKAGE_INFO',
                payload: {
                    lp_name,
                    lp_version,
                    has_package,
                },
            });
        } catch (err) {
            console.error('Failed to load package detail', err);
        } finally {
            dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'layout', loading: false } });
        }
    }, [manageStoreId]);

    const autoGeneratePreview = async (packageId: number) => {
        if (!state.previewBarcode) {
            dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'regenerate', open: true } });
            return;
        }
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'regenerate', loading: true } });
        showLoading();
        try {
            const corpId = await localforage.getItem<number>('corporation_id');
            if (!corpId) return;

            const {
                data: { data, isSuccess },
            } = await generatePreviewAPI(corpId, {
                layout_package_id: packageId,
                products: [state.previewBarcode],
            });
            if (!isSuccess) throw new Error();

            const resultMap: Record<string, [boolean, string, string]> = data.result;
            const errors = Object.entries(resultMap)
                .filter(([, [ok]]) => !ok)
                .map(([key, [, msg]]) => ({
                    key,
                    message: msg.replace(/[\r\n]+/g, ' '),
                }));
            if (errors.length > 0) {
                dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'previewError', open: true } });
                dispatch({ type: 'SET_PREVIEW_ERROR', payload: errors });
                return;
            }

            setNotification({ message: t('Preview regenerated!'), severity: 'success' });
            await fetchDetail();
        } catch {
            setNotification({ message: t('Failed to regenerate preview.'), severity: 'error' });
        } finally {
            dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'regenerate', loading: false } });
            hideLoading();
        }
    };

    const formateMultiInfo = () => {
        const { sub_layouts, multi_display_type_relations } = state.layoutInfo as LayoutPackageDetails;

        let formateLayouts: FullLayoutItem[] = [];
        let relations = {};
        let maxExisting = 1;

        if (sub_layouts && multi_display_type_relations) {
            formateLayouts = sub_layouts.map((l) => {
                const { colors, bpp, resolution } = parseSizeAndColorsFromXml(l.sublayout_fields);
                return {
                    id: String(l.id),
                    name: l.sublayout_name,
                    purpose: String(l.sublayout_purpose),
                    description: '',
                    xmlContent: l.sublayout_fields,
                    images: l.sublayout_img ? [l.sublayout_img] : [],
                    number: String(l.id),
                    colors,
                    size: '',
                    bpp,
                    resolution,
                };
            });

            relations = multi_display_type_relations ?? {};
            maxExisting = formateLayouts.reduce((m, l) => Math.max(m, Number(l.id)), 0);
        }

        return {
            layouts: formateLayouts,
            relations,
            maxExisting,
        };
    };

    const formateSingleInfo = () => {
        const { layouts, display_type_relations } = state.layoutInfo as LayoutPackageDetails;

        let formateLayouts: FullLayoutItem[] = [];
        let relations = {};
        let maxExisting = 1;

        if (layouts && display_type_relations) {
            formateLayouts = layouts.map((l) => ({
                id: l.id.toString(),
                name: l.layout_name,
                purpose: String(l.layout_purpose),
                description: '',
                xmlContent: l.layout_fields,
                images: l.layout_img ? [l.layout_img] : [],
                size: l.layout_size,
                number: l.id.toString(),
                bpp: l.bpp,
                colors: (l.color_profile || '')
                    .split('')
                    .map((c) =>
                        c === 'b'
                            ? '#000000'
                            : c === 'w'
                              ? '#FFFFFF'
                              : c === 'r'
                                ? '#FF0000'
                                : c === 'y'
                                  ? '#FFFF00'
                                  : '',
                    )
                    .filter(Boolean),
            }));

            relations = display_type_relations ?? {};
            maxExisting = formateLayouts.reduce((m, l) => Math.max(m, Number(l.id)), 0);
        }

        return {
            layouts: formateLayouts,
            relations,
            maxExisting,
        };
    };

    const optionsWithLabel = useMemo(() => {
        return state.displayOptions.map((o) => ({ ...o, label: o.label }));
    }, [state.displayOptions]);

    const getSinglePayload = () => {
        const { layoutsById, relations } = state.single;
        const layouts = Object.entries(layoutsById).map(([, o]) => ({
            id: Number(o.id),
            layout_capacity: 1,
            layout_name: o.name,
            layout_purpose: +o.purpose,
            sub_layout_pos_size: '',
            layout_fields: xmlEscapedToBase64(o.xmlContent),
        }));
        const relationsMap: SingleRelationsMap = {};
        Object.keys(relations).map((key: string) => {
            const { list, default_layout } = relations[key];
            relationsMap[key] = list.map((o) => Number(o.id));
            const index = findIndex(list, (o) => o.uuid === default_layout);
            relationsMap[`${key}_conf`] = {
                default_layout: index,
            };
        });
        return {
            layouts,
            display_type_relations: relationsMap,
        };
    };
    const getMultiPayload = () => {
        const { layoutsById, relations } = state.multi;
        const sub_layouts = Object.entries(layoutsById).map(([, o]) => ({
            id: Number(o.id),
            layout_capacity: 1,
            sublayout_name: o.name,
            sublayout_purpose: +o.purpose,
            sub_layout_pos_size: '',
            sublayout_fields: xmlEscapedToBase64(o.xmlContent),
        }));
        const relationsMap: MultiRelationsMap = {};
        Object.keys(relations).forEach((key: string) => {
            Object.keys(relations[key]).forEach((slug) => {
                const { list, default_layout } = relations[key][slug];
                const index = findIndex(list, (o) => o.uuid === default_layout);
                relationsMap[key] = {
                    ...relationsMap[key],
                    [slug]: list.map((o) => Number(o.id)),
                };
                relationsMap[`${key}_conf`] = {
                    ...relationsMap[`${key}_conf`],
                    [slug]: {
                        default_layout: index,
                    },
                };
            });
        });
        return {
            sub_layouts,
            multi_display_type_relations: relationsMap,
        };
    };
    return {
        id,
        isNew,
        optionsWithLabel,
        fetchCategories,
        fetchStoreDetail,
        fetchDetail,
        autoGeneratePreview,
        formateMultiInfo,
        formateSingleInfo,
        getSinglePayload,
        getMultiPayload,
    };
};
